/**
 * api/app.ts —— 应用级能力
 *
 * 这里放的是"关于应用本身"的操作：看版本、看能力、清数据、载演示数据。
 * 清数据与载演示数据都是**不可逆**的，所以都挂了二次确认。
 */

import { registry, type ObjectDef } from '../registry';
import { uiStore, pushOverlay } from '@/core/ui-store';
import { handles } from '../handles';
import { dbClearAll } from '@/data/db';
import { eventBus } from '@/core/event-bus';
import { loadDemoData } from '@/data/demo-data';
import { settingsRepo } from '@/settings/settings';

/** 版本号写死在这里，构建时不需要注入 —— PoC 阶段够用 */
const VERSION = '0.1.0';

/**
 * 把能力清单渲染成一段给人看的话。
 *
 * ⚠️ 这里**不是**第二份能力文档 —— 数据仍然全部来自 registry，
 * 只是把结构化清单组织成句子。新增 API 时这里一个字都不用改，
 * 因为它遍历的是 registry.listObjects() 的实时结果。
 */
function renderHelp(): string {
  const lines = registry.describeForUser().map((text) => `· ${text}`);
  return [
    `我是「灵讯」的助手（版本 ${VERSION}），你可以直接跟我说要做的事，比如：`,
    ...lines,
    '',
    '直接说人话就行，我会自己翻译成操作。',
  ].join('\n');
}

export const appApi = {
  name: 'app',
  desc: '应用自身：版本、能力清单、数据管理',
  userDesc: '关于应用本身 —— 看版本、看我能做什么、载入演示数据、清空数据',
  methods: [
    {
      name: 'version',
      kind: 'query',
      desc: '读应用的版本号。',
      params: [],
      successText: `应用版本是 ${VERSION}`,
      run: () => ({ value: VERSION }),
    },
    {
      name: 'help',
      // 注意：这里**刻意不标 query**。
      // 能力清单是确定性的、程序自己就能写成人话（就是下面 renderHelp），
      // 没必要绕一圈让 AI 复述 —— 那样既慢（清单很长，模型要读半天）
      // 又容易失真。它的 successText 本身就是完整答案。
      desc:
        '列出这个应用会做的所有事。用户问「你能做什么」「你会哪些功能」「帮助」时用这个。',
      params: [],
      successText: () => renderHelp(),
      run: () => {
        const objects = registry.listObjects().map((o) => ({
          name: o.name,
          desc: o.desc,
          methods: o.methods.map((m) => m.name),
        }));
        // value 仍然给出结构化清单，供调试/后续扩展使用
        return { value: { version: VERSION, objects } };
      },
    },
    {
      name: 'clearData',
      desc: '清空本地的全部数据（联系人、会话、消息、文件）。用户明确说「清空数据」「全部删掉重来」时才用。',
      params: [],
      successText: '已清空全部数据',
      requireConfirm: () =>
        '这会删除本机保存的全部联系人、聊天记录和文件，且无法恢复。确定要继续吗？',
      run: async () => {
        await dbClearAll();
        handles.clear();
        eventBus.emit('data:cleared', {});
        return {};
      },
    },
    {
      name: 'loadDemoData',
      desc: '载入一批演示用的联系人和聊天记录。用户说「载入演示数据」「来点示例数据」时用这个。',
      params: [],
      successText: '已载入演示数据',
      requireConfirm: () => '载入演示数据会往当前库里追加一批示例联系人和聊天记录，确定吗？',
      run: async () => {
        const r = await loadDemoData();
        eventBus.emit('data:demo-loaded', { contacts: r.contacts, sessions: r.sessions });
        return { text: `已载入演示数据：${r.contacts} 位联系人、${r.sessions} 个会话` };
      },
    },
    {
      name: 'openDebug',
      desc: '打开调试页，可以查看能力清单和 AI 调用日志。',
      params: [],
      successText: '已打开调试页',
      run: () => {
        pushOverlay({ kind: 'debug', refId: '' });
        return {};
      },
    },
    {
      name: 'openSettings',
      desc: '跳到设置页。等价于 nav.go(\'settings\')，保留是为了让模型有个更直观的说法。',
      params: [],
      successText: '已打开设置',
      run: () => {
        uiStore.set({ tab: 'settings' });
        settingsRepo.setApp({ lastTab: 'settings' });
        eventBus.emit('ui:tab-changed', { tab: 'settings' });
        return {};
      },
    },
  ],
} satisfies ObjectDef;
